import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { FileEarmarkPdfFill } from 'react-bootstrap-icons';
import JoditEditor from 'jodit-react';
import { Check, GripVertical, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react';
import isEqual from 'lodash/isEqual';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import AlertModal from '../AlertModal';
import RewriteQaRulesModal from './RewriteQaRulesModal';

const EDITOR_BUTTONS = ['bold', 'italic', 'underline', '|', 'ul', 'ol', '|', 'undo', 'redo'];

const JODIT_CONFIG = {
  readonly: false,
  placeholder: 'Write verification rules in plain English…',
  height: 360,
  toolbar: true,
  toolbarButtonSize: 'medium',
  spellcheck: true,
  language: 'en',
  statusbar: false,
  toolbarAdaptive: false,
  toolbarSticky: false,
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_clear_html',
  buttons: EDITOR_BUTTONS,
  buttonsMD: EDITOR_BUTTONS,
  buttonsSM: EDITOR_BUTTONS,
  buttonsXS: EDITOR_BUTTONS,
  disablePlugins: ['source', 'image', 'video', 'file', 'table', 'link', 'symbols', 'font', 'brush'],
  cleanHTML: {
    fillEmptyParagraph: false,
    denyTags: 'script,style,iframe,img,video,audio,table,code,pre,svg'
  }
};

const GLOBAL_SECTION_KEY = 'global';
const ICON_BUTTON_STYLE = { minWidth: 28, minHeight: 28, width: 28, height: 28 };

let documentLocalId = 0;
const nextDocumentLocalId = () => `qa-doc-${++documentLocalId}`;

const moveItem = (list, fromIndex, toIndex) => {
  if (fromIndex === toIndex || fromIndex == null || toIndex == null) return list;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const shiftIndex = (current, fromIndex, toIndex) => {
  if (current == null) return current;
  if (current === fromIndex) return toIndex;
  if (fromIndex < toIndex && current > fromIndex && current <= toIndex) return current - 1;
  if (toIndex < fromIndex && current >= toIndex && current < fromIndex) return current + 1;
  return current;
};

const stripDocument = ({ _localId, ...doc }) => doc;

const stampDocuments = (documents = [], previous = []) =>
  documents.map((doc, index) => ({
    ...doc,
    _localId: doc._localId || previous[index]?._localId || nextDocumentLocalId()
  }));

const stampRuleSet = (ruleSet, previous) => {
  if (!ruleSet) return ruleSet;
  const previousSections = previous?.sections || [];
  return {
    ...ruleSet,
    sections: (ruleSet.sections || []).map(section => {
      const previousSection = previousSections.find(entry => entry.key === section.key);
      return {
        ...section,
        documents: stampDocuments(section.documents, previousSection?.documents)
      };
    })
  };
};

const sectionsForCompare = (sections = []) =>
  sections.map(section => ({
    ...section,
    documents: (section.documents || []).map(stripDocument)
  }));

const findIndexAtPoint = (itemRefs, order, x, y, activeId) => {
  for (let index = 0; index < order.length; index++) {
    const id = order[index];
    if (id === activeId) continue;
    const node = itemRefs.get(id);
    if (!node?.isConnected) continue;
    const rect = node.getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return index;
  }
  return null;
};

const showDragPreview = drag => {
  const preview = drag.node.cloneNode(true);
  preview.querySelectorAll('[id]').forEach(child => child.removeAttribute('id'));
  Object.assign(preview.style, {
    position: 'fixed',
    left: 0,
    top: 0,
    width: `${drag.rect.width}px`,
    height: `${drag.rect.height}px`,
    pointerEvents: 'none',
    zIndex: 2000,
    filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.22))'
  });
  document.body.appendChild(preview);
  drag.preview = preview;
};

const moveDragPreview = (drag, x, y) => {
  if (!drag.preview) return;
  const left = x - (drag.startX - drag.rect.left);
  const top = y - (drag.startY - drag.rect.top);
  drag.preview.style.transform = `translate(${left}px, ${top}px) scale(1.03)`;
};

const endDrag = drag => {
  drag.preview?.remove();
  Object.assign(document.body.style, { userSelect: '', cursor: '' });
};

const useLiveReorder = onMove => {
  const itemRefs = useRef(new Map());
  const dragRef = useRef(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const [dragId, setDragId] = useState(null);
  const [layoutTick, setLayoutTick] = useState(0);

  useLayoutEffect(() => {
    if (dragRef.current) dragRef.current.waiting = false;
  }, [layoutTick]);

  useEffect(() => {
    const onPointerMove = event => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      moveDragPreview(drag, event.clientX, event.clientY);
      if (drag.waiting) return;
      if (!drag.active) {
        if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 4) return;
        drag.active = true;
        setDragId(drag.id);
        Object.assign(document.body.style, { userSelect: 'none', cursor: 'grabbing' });
        showDragPreview(drag);
        moveDragPreview(drag, event.clientX, event.clientY);
      }
      const nextIndex = findIndexAtPoint(itemRefs.current, drag.order, event.clientX, event.clientY, drag.id);
      if (nextIndex == null || nextIndex === drag.index) return;
      const from = drag.index;
      drag.waiting = true;
      drag.index = nextIndex;
      drag.order = moveItem(drag.order, from, nextIndex);
      setLayoutTick(tick => tick + 1);
      onMoveRef.current(from, nextIndex);
    };

    const finish = event => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      dragRef.current = null;
      endDrag(drag);
      setDragId(null);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      if (!dragRef.current) return;
      endDrag(dragRef.current);
      dragRef.current = null;
    };
  }, []);

  const beginDrag = (event, { id, index, order }) => {
    if (event.button != null && event.button !== 0) return;
    const node = itemRefs.current.get(id);
    if (!id || order.length < 2 || !node) return;
    dragRef.current = {
      id,
      node,
      rect: node.getBoundingClientRect(),
      index,
      order: [...order],
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      waiting: false
    };
    event.preventDefault();
    event.stopPropagation();
  };

  const setItemRef = (id, node) => {
    if (!id) return;
    if (node) {
      itemRefs.current.set(id, node);
      return;
    }
    const current = itemRefs.current.get(id);
    if (current && !current.isConnected) itemRefs.current.delete(id);
  };

  return { dragId, setItemRef, beginDrag };
};

const phraseKey = phrase => String(phrase || '').trim().toLowerCase();

const PhraseTags = ({
  phrases = [],
  organizePhrases,
  organizeEnabled = false,
  onChange,
  reorderEnabled = true
}) => {
  const [draft, setDraft] = useState('');
  const phrasesRef = useRef(phrases);
  phrasesRef.current = phrases;
  const organizeRef = useRef(organizePhrases);
  organizeRef.current = organizePhrases;

  const emit = (nextPhrases, nextOrganize) => {
    if (!organizeEnabled || nextOrganize === undefined) {
      onChange({ fileNameContains: nextPhrases });
      return;
    }
    const allowed = new Set(nextOrganize.map(phraseKey).filter(Boolean));
    onChange({
      fileNameContains: nextPhrases,
      organizeFileNameContains: nextPhrases.filter(phrase => allowed.has(phraseKey(phrase)))
    });
  };

  const { setItemRef, beginDrag } = useLiveReorder((from, to) => {
    if (!reorderEnabled) return;
    const nextPhrases = moveItem(phrasesRef.current, from, to);
    emit(
      nextPhrases,
      organizeEnabled && Array.isArray(organizeRef.current) ? organizeRef.current : undefined
    );
  });

  const addPhrase = value => {
    const phrase = String(value || '').trim();
    if (!phrase) return;
    if (phrases.some(existing => phraseKey(existing) === phraseKey(phrase))) {
      setDraft('');
      return;
    }
    const nextPhrases = [...phrases, phrase];
    if (organizeEnabled) {
      emit(nextPhrases, Array.isArray(organizePhrases) ? [...organizePhrases, phrase] : nextPhrases);
    } else {
      emit(nextPhrases);
    }
    setDraft('');
  };

  const removePhrase = phrase => {
    const nextPhrases = phrases.filter(existing => existing !== phrase);
    if (organizeEnabled && Array.isArray(organizePhrases)) {
      emit(
        nextPhrases,
        organizePhrases.filter(entry => phraseKey(entry) !== phraseKey(phrase))
      );
      return;
    }
    emit(nextPhrases);
  };

  const toggleOrganize = phrase => {
    if (!organizeEnabled) return;
    const current = Array.isArray(organizePhrases) ? organizePhrases : [...phrases];
    const key = phraseKey(phrase);
    const checked = current.some(entry => phraseKey(entry) === key);
    emit(
      phrases,
      checked ? current.filter(entry => phraseKey(entry) !== key) : [...current, phrase]
    );
  };

  return (
    <div
      className="d-flex flex-wrap align-items-center border rounded px-2 py-1 bg-white"
      style={{ gap: 6, minHeight: 38 }}
    >
      {phrases.map((phrase, index) => {
        const checked =
          !Array.isArray(organizePhrases) ||
          organizePhrases.some(entry => phraseKey(entry) === phraseKey(phrase));
        return (
          <span
            key={phrase}
            ref={node => setItemRef(phrase, node)}
            title={reorderEnabled ? 'Drag to reorder' : undefined}
            className="d-inline-flex align-items-center font-weight-bold"
            style={{
              background: '#f3f4f6',
              color: '#374151',
              borderRadius: 999,
              padding: reorderEnabled || organizeEnabled ? '2px 8px 2px 4px' : '2px 8px',
              fontSize: 11,
              gap: 4,
              maxWidth: '100%',
              cursor: reorderEnabled && phrases.length > 1 ? 'grab' : 'default',
              userSelect: reorderEnabled ? 'none' : undefined,
              touchAction: reorderEnabled ? 'none' : undefined
            }}
            onPointerDown={event => {
              if (!reorderEnabled) return;
              if (event.target.closest('[data-phrase-remove], [data-phrase-organize]')) return;
              beginDrag(event, { id: phrase, index, order: phrasesRef.current });
            }}
          >
            {organizeEnabled && (
              <input
                type="checkbox"
                data-phrase-organize
                className="flex-shrink-0 m-0"
                style={{ width: 12, height: 12, cursor: 'pointer' }}
                checked={checked}
                title={checked ? 'Used for auto-organize' : 'Not used for auto-organize'}
                onPointerDown={event => event.stopPropagation()}
                onChange={() => toggleOrganize(phrase)}
              />
            )}
            {reorderEnabled && <GripVertical size={10} className="flex-shrink-0" style={{ color: '#9ca3af' }} />}
            <span className="text-truncate">{phrase}</span>
            <button
              type="button"
              data-phrase-remove
              className="btn p-0 border-0 bg-transparent d-flex align-items-center text-muted"
              title={`Remove ${phrase}`}
              onPointerDown={event => event.stopPropagation()}
              onClick={() => removePhrase(phrase)}
            >
              <X size={11} />
            </button>
          </span>
        );
      })}
      <input
        className="border-0 flex-grow-1 bg-transparent"
        style={{ outline: 'none', minWidth: 92, fontSize: 13, lineHeight: 1.4 }}
        value={draft}
        placeholder={phrases.length ? 'Add phrase' : 'Type a phrase and press Enter'}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addPhrase(draft);
          }
          if (e.key === 'Backspace' && !draft && phrases.length) {
            removePhrase(phrases[phrases.length - 1]);
          }
        }}
        onBlur={() => addPhrase(draft)}
      />
    </div>
  );
};

const DocumentCard = ({
  doc,
  index,
  isEditing,
  isDragging,
  reorderEnabled = true,
  organizeEnabled = false,
  onGripPointerDown,
  onEdit,
  onDone,
  onDelete,
  onChange
}) => {
  const phraseCount = (doc.fileNameContains || []).length;
  const displayName = doc.name?.trim() || 'Untitled document';

  return (
    <div
      className="bg-white border d-flex flex-column"
      style={{
        borderRadius: 12,
        padding: '8px 10px',
        gap: 10,
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        borderColor: isEditing ? '#111827' : '#dbd9d1',
        boxSizing: 'border-box'
      }}
    >
      <div className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
        <div
          title={reorderEnabled ? 'Drag to reorder' : undefined}
          className="d-flex align-items-center flex-grow-1"
          style={{
            gap: 8,
            minWidth: 0,
            cursor: reorderEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: reorderEnabled ? 'none' : undefined,
            touchAction: reorderEnabled ? 'none' : undefined
          }}
          onPointerDown={reorderEnabled ? onGripPointerDown : undefined}
        >
          {reorderEnabled && <GripVertical size={14} className="flex-shrink-0 text-muted" />}
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#ffe9ea' }}
          >
            <FileEarmarkPdfFill size={16} className="text-danger" />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="text-truncate font-weight-bold" style={{ fontSize: 13 }} title={displayName}>
              {displayName}
            </div>
            {!isEditing && (
              <div className="text-muted" style={{ fontSize: 11, lineHeight: 1.2 }}>
                {phraseCount} phrase{phraseCount === 1 ? '' : 's'}
                {doc.required ? ' · Required' : ''}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center flex-shrink-0" style={{ gap: 6 }}>
          <Button
            type="button"
            variant="light"
            size="sm"
            className="p-0 rounded-circle border d-flex align-items-center justify-content-center"
            style={ICON_BUTTON_STYLE}
            title={isEditing ? 'Done' : 'Edit'}
            onClick={isEditing ? onDone : onEdit}
          >
            {isEditing ? <Check size={14} className="text-dark" /> : <Pencil size={13} className="text-dark" />}
          </Button>
          <Button
            type="button"
            variant="light"
            size="sm"
            className="p-0 rounded-circle border d-flex align-items-center justify-content-center"
            style={ICON_BUTTON_STYLE}
            title="Delete"
            onClick={onDelete}
          >
            <Trash2 size={13} className="text-danger" />
          </Button>
        </div>
      </div>

      {isEditing && (
        <>
          <Form.Group className="mb-0">
            <Form.Label className="tinyFont text-muted mb-1">Name</Form.Label>
            <Form.Control
              size="sm"
              value={doc.name}
              placeholder="County Tax Bill"
              autoFocus={!doc.name}
              onChange={e => onChange({ name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="tinyFont text-muted mb-1">Filename contains</Form.Label>
            {organizeEnabled && (
              <p className="text-muted mb-1" style={{ fontSize: 11, lineHeight: 1.35 }}>
                Check a phrase to use it for auto-organize. Drag documents to set tab order.
              </p>
            )}
            <PhraseTags
              phrases={doc.fileNameContains}
              organizePhrases={doc.organizeFileNameContains}
              organizeEnabled={organizeEnabled}
              reorderEnabled={reorderEnabled}
              onChange={onChange}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id={`qa-doc-required-${index}`}
            label="Required"
            className="tinyFont mb-0"
            checked={Boolean(doc.required)}
            onChange={e => onChange({ required: e.target.checked })}
          />
        </>
      )}
    </div>
  );
};

const DocumentGrid = ({
  documents,
  editingIndex,
  reorderEnabled = true,
  organizeEnabled = false,
  onEdit,
  onDone,
  onDelete,
  onChange,
  onReorder
}) => {
  const { dragId, setItemRef, beginDrag } = useLiveReorder(onReorder);

  return (
    <div
      className="mb-4"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 10,
        alignItems: 'start'
      }}
    >
      {documents.map((doc, index) => (
        <div key={doc._localId || index} ref={node => setItemRef(doc._localId, node)} style={{ minWidth: 0 }}>
          <DocumentCard
            doc={doc}
            index={index}
            isEditing={editingIndex === index}
            isDragging={reorderEnabled && dragId === doc._localId}
            reorderEnabled={reorderEnabled}
            organizeEnabled={organizeEnabled}
            onGripPointerDown={event => {
              if (!reorderEnabled) return;
              beginDrag(event, {
                id: doc._localId,
                index,
                order: documents.map(item => item._localId)
              });
            }}
            onEdit={() => onEdit(index)}
            onDone={onDone}
            onDelete={() => onDelete(index)}
            onChange={patch => onChange(index, patch)}
          />
        </div>
      ))}
    </div>
  );
};

const LienSearchQaRules = forwardRef(
  ({ reportType = 'Lien Search', setActiveTabItemsMetadata, onNavigateToTab }, ref) => {
    const reorderEnabled = reportType !== 'Estoppel';
    const organizeEnabled = reportType === 'Lien Search';
    const [ruleSet, setRuleSet] = useState(null);
    const [initialRuleSet, setInitialRuleSet] = useState(null);
    const [selectedKey, setSelectedKey] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
    const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [showRewriteModal, setShowRewriteModal] = useState(false);
    const [rewriting, setRewriting] = useState(false);
    const [editorNonce, setEditorNonce] = useState(0);
    const showUnsavedChangesAlertRef = useRef(false);

    const hasUnsavedChanges = useMemo(
      () =>
        Boolean(
          ruleSet &&
            initialRuleSet &&
            !isEqual(sectionsForCompare(ruleSet.sections), sectionsForCompare(initialRuleSet.sections))
        ),
      [ruleSet, initialRuleSet]
    );

    const selectedSection = useMemo(
      () => (ruleSet?.sections || []).find(section => section.key === selectedKey) || null,
      [ruleSet, selectedKey]
    );

    useEffect(() => {
      setEditingIndex(null);
      setEditorNonce(0);
    }, [selectedKey]);

    useEffect(() => {
      showUnsavedChangesAlertRef.current = showUnsavedChangesAlert;
    }, [showUnsavedChangesAlert]);

    const loadRules = useCallback(async () => {
      setLoading(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.REPORT_QA_RULESET(reportType),
        method: 'GET'
      });
      setLoading(false);

      if (error) {
        toast.error(error);
        setRuleSet(null);
        setInitialRuleSet(null);
        setActiveTabItemsMetadata?.(prev => ({ ...prev, count: 0, loading: false }));
        return;
      }

      const stamped = stampRuleSet(response);
      setRuleSet(stamped);
      setInitialRuleSet(stamped);
      setSelectedKey(prev => prev || response?.sections?.[0]?.key || '');
      setActiveTabItemsMetadata?.(prev => ({ ...prev, count: response?.sections?.length || 0, loading: false }));
    }, [reportType, setActiveTabItemsMetadata]);

    useEffect(() => {
      setActiveTabItemsMetadata?.(prev => ({ ...prev, count: 0, loading: true }));
      void loadRules();
    }, [loadRules, setActiveTabItemsMetadata]);

    const updateSelectedSection = updater => {
      setRuleSet(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map(section => (section.key === selectedKey ? updater(section) : section))
        };
      });
    };

    const handleSave = async () => {
      if (!ruleSet) return false;
      setSaving(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.REPORT_QA_RULESET(reportType),
        method: 'PUT',
        requestBody: {
          sections: (ruleSet.sections || []).map(section => ({
            ...section,
            documents: (section.documents || []).map(stripDocument)
          }))
        }
      });
      setSaving(false);

      if (error) {
        toast.error(error);
        return false;
      }

      const stamped = stampRuleSet(response, ruleSet);
      setRuleSet(stamped);
      setInitialRuleSet(stamped);
      toast.success('QA rules saved. The next analysis run will use these rules.');
      return true;
    };

    const requestBackOfficeTabChange = useCallback(
      nextTabId => {
        if (!hasUnsavedChanges) return false;
        if (showUnsavedChangesAlertRef.current) return true;
        setPendingLeaveAction({ type: 'back-office-tab', nextTabId });
        setShowUnsavedChangesAlert(true);
        return true;
      },
      [hasUnsavedChanges]
    );

    useImperativeHandle(
      ref,
      () => ({
        requestBackOfficeTabChange,
        save: handleSave
      }),
      [requestBackOfficeTabChange, handleSave]
    );

    useEffect(() => {
      setActiveTabItemsMetadata?.(prev => ({
        ...prev,
        saving,
        saveDisabled: saving || !hasUnsavedChanges || loading || !ruleSet
      }));
    }, [hasUnsavedChanges, loading, ruleSet, saving, setActiveTabItemsMetadata]);

    const handleConfirmLeave = async ({ saveFirst }) => {
      if (saveFirst) {
        const saved = await handleSave();
        if (!saved) return;
      }
      const nextTabId = pendingLeaveAction?.nextTabId;
      setShowUnsavedChangesAlert(false);
      setPendingLeaveAction(null);
      if (nextTabId) onNavigateToTab?.(nextTabId);
    };

    const handleRewriteWithAi = async instruction => {
      if (!selectedSection) return;
      setRewriting(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.REPORT_QA_RULESET_REWRITE(reportType, selectedSection.key),
        method: 'POST',
        requestBody: {
          instruction,
          currentLogic: selectedSection.verificationLogic || '',
          documents: (selectedSection.documents || []).map(stripDocument)
        }
      });
      setRewriting(false);

      if (error) {
        toast.error(error);
        return;
      }

      updateSelectedSection(section => ({
        ...section,
        verificationLogic: response?.verificationLogic || section.verificationLogic,
        documents:
          selectedSection.key === GLOBAL_SECTION_KEY
            ? []
            : Array.isArray(response?.documents)
            ? stampDocuments(response.documents)
            : section.documents
      }));
      setEditingIndex(null);
      setEditorNonce(prev => prev + 1);
      setShowRewriteModal(false);
      toast.success('Rules updated. Review them, then Save when ready.');
    };

    const addDocument = () => {
      if (!selectedSection || selectedSection.key === GLOBAL_SECTION_KEY) return;
      const nextIndex = (selectedSection?.documents || []).length;
      updateSelectedSection(section => ({
        ...section,
        documents: [
          ...(section.documents || []),
          { name: '', fileNameContains: [], required: false, _localId: nextDocumentLocalId() }
        ]
      }));
      setEditingIndex(nextIndex);
    };

    const updateDocument = (index, patch) => {
      updateSelectedSection(section => ({
        ...section,
        documents: section.documents.map((doc, docIndex) => (docIndex === index ? { ...doc, ...patch } : doc))
      }));
    };

    const removeDocument = index => {
      updateSelectedSection(section => ({
        ...section,
        documents: section.documents.filter((_, docIndex) => docIndex !== index)
      }));
    };

    const reorderDocuments = (fromIndex, toIndex) => {
      if (fromIndex == null || fromIndex === toIndex) return;
      updateSelectedSection(section => ({
        ...section,
        documents: moveItem(section.documents || [], fromIndex, toIndex)
      }));
      setEditingIndex(prev => shiftIndex(prev, fromIndex, toIndex));
    };

    if (loading) {
      return (
        <div className="d-flex align-items-center justify-content-center py-5">
          <Spinner animation="border" size="sm" className="mr-2" />
          <span className="text-muted">Loading QA rules…</span>
        </div>
      );
    }

    if (!ruleSet) {
      return (
        <div className="px-4 py-4 text-muted">
          QA rules have not been seeded yet. Run <code>node backend/migrations/seedReportQaRuleSets.js</code> from the
          backend folder.
        </div>
      );
    }

    return (
      <div className="d-flex flex-column h-100 px-md-4 pb-4">
        <div
          className="d-flex align-items-center bg-white border-bottom py-2 flex-wrap"
          style={{ gap: 4, borderRadius: '14px 14px 0 0' }}
        >
          {(ruleSet.sections || []).map(section => {
            const isActive = section.key === selectedKey;
            const isGlobal = section.key === GLOBAL_SECTION_KEY;
            const count = (section.documents || []).length;
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setSelectedKey(section.key)}
                className={`btn btn-sm border-0 shadow-none font-weight-bold xmidFont rounded-pill px-3 py-1 ${
                  !isActive ? 'hover-muted' : ''
                }`}
                style={{
                  background: isActive ? '#111827' : 'transparent',
                  color: isActive ? 'white' : '#6b7280',
                  transition: 'background 0.12s, color 0.12s'
                }}
              >
                {section.label}
                {!isGlobal && (
                  <span className="ml-1" style={{ fontSize: 11, opacity: 0.7 }}>
                    ({count})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div
          className="flex-grow-1 bg-white p-3"
          style={{ overflowY: 'auto', minWidth: 0, borderRadius: '0 0 14px 14px' }}
        >
          {selectedSection ? (
            <>
              {selectedSection.key === GLOBAL_SECTION_KEY ? (
                <p className="text-muted tinyFont mb-3">These rules apply to every section when QA runs.</p>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="font-weight-bold mb-0">Documents</h6>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="d-flex align-items-center"
                      style={{ gap: 6 }}
                      onClick={addDocument}
                    >
                      <Plus size={14} />
                      Add document
                    </Button>
                  </div>

                  {(selectedSection.documents || []).length ? (
                    <DocumentGrid
                      key={selectedSection.key}
                      documents={selectedSection.documents}
                      editingIndex={editingIndex}
                      reorderEnabled={reorderEnabled}
                      organizeEnabled={organizeEnabled}
                      onEdit={setEditingIndex}
                      onDone={() => setEditingIndex(null)}
                      onDelete={index => {
                        setEditingIndex(prev => {
                          if (prev === index) return null;
                          if (prev != null && prev > index) return prev - 1;
                          return prev;
                        });
                        removeDocument(index);
                      }}
                      onChange={updateDocument}
                      onReorder={reorderDocuments}
                    />
                  ) : (
                    <p className="text-muted tinyFont mb-4">
                      No documents yet. Add the PDFs this section should match by filename.
                    </p>
                  )}
                </>
              )}

              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="font-weight-bold mb-0">Verification logic</h6>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="d-flex align-items-center"
                  style={{ gap: 6 }}
                  disabled={rewriting}
                  onClick={() => setShowRewriteModal(true)}
                >
                  <Sparkles size={14} />
                  Rewrite with AI
                </Button>
              </div>
              <JoditEditor
                key={`${selectedSection.key}-${editorNonce}`}
                value={selectedSection.verificationLogic || ''}
                config={JODIT_CONFIG}
                tabIndex={1}
                onBlur={value => updateSelectedSection(section => ({ ...section, verificationLogic: value }))}
                onChange={value => {
                  updateSelectedSection(section => ({ ...section, verificationLogic: value }));
                }}
              />
            </>
          ) : (
            <p className="text-muted">Select a section.</p>
          )}
        </div>

        <RewriteQaRulesModal
          show={showRewriteModal}
          sectionLabel={selectedSection?.label}
          rewriting={rewriting}
          onHide={() => {
            if (!rewriting) setShowRewriteModal(false);
          }}
          onSubmit={instruction => void handleRewriteWithAi(instruction)}
        />

        <AlertModal
          show={showUnsavedChangesAlert}
          onHide={() => setShowUnsavedChangesAlert(false)}
          onDismissClick={() => void handleConfirmLeave({ saveFirst: false })}
          onContinueClick={() => void handleConfirmLeave({ saveFirst: true })}
          alertText="You have unsaved QA rule changes."
          continueButtonText="Save & leave"
          dismissButtonText="Leave without saving"
          continueButtonVariant="success"
          dismissButtonVariant="outline-secondary"
          showProgress={saving}
          progressText="Saving…"
        />
      </div>
    );
  }
);

LienSearchQaRules.displayName = 'LienSearchQaRules';

export default LienSearchQaRules;
